/**
 * GA4 Item Mapper Utility
 * Provides consistent functions for mapping product data to GA4 e-commerce item format
 */

/**
 * Map a product to GA4 item format
 * Handles both Algolia product format and API product format
 * 
 * @param {Object} product - The product data
 * @param {number} index - Position in list (optional)
 * @returns {Object} GA4 formatted item
 */
export const mapProductToGA4Item = (product, index = 0) => {
  // Handle both Algolia product format and API product format
  const isAlgoliaFormat = !product.master && product.manufacturer_id;
  
  if (isAlgoliaFormat) {
    return {
      item_id: product.manufacturer_id || '',
      item_name: product.title || '',
      item_brand: product.brand || '',
      item_category: product.breadcrumb_taxons?.[0]?.name || product.category || '',
      item_category2: product.breadcrumb_taxons?.[1]?.name || '',
      item_category3: product.breadcrumb_taxons?.[2]?.name || '',
      item_variant: product.selected_variant_id || '',
      price: product.price_min || 0,
      index: index,
      quantity: 1
    };
  }
  
  // Regular API product format
  return {
    item_id: product.master?.sku || '',
    item_name: product.name || '',
    item_brand: product.brand || '',
    item_category: product.breadcrumb_taxons?.[0]?.name || '',
    item_category2: product.breadcrumb_taxons?.[1]?.name || '',
    item_category3: product.breadcrumb_taxons?.[2]?.name || '',
    item_variant: product.variants?.[0]?.option_values?.[0]?.name || '',
    price: parseFloat(product.display_price?.replace(/[^0-9.]/g, '')) || 0,
    index: index,
    quantity: 1
  };
};

/**
 * Map a cart line item to GA4 item format
 * 
 * @param {Object} lineItem - The cart line item
 * @param {number} index - Position in cart (optional)
 * @returns {Object} GA4 formatted item
 */
export const mapCartItemToGA4Item = (lineItem, index = 0) => {
  return {
    item_id: lineItem.variant?.master_sku || '',
    item_name: lineItem.variant?.name || '',
    item_brand: lineItem.variant?.brand || '',
    item_category: lineItem.breadcrumb_taxons?.[0]?.name || '',
    item_category2: lineItem.breadcrumb_taxons?.[1]?.name || '',
    item_category3: lineItem.breadcrumb_taxons?.[2]?.name || '',
    item_variant: lineItem.variant?.option_values?.[0]?.name || '',
    price: parseFloat(lineItem.price) || 0,
    quantity: parseInt(lineItem.quantity, 10) || 1,
    index: index
  };
};

/**
 * Map an order line item to GA4 item format
 * 
 * @param {Object} orderLineItem - The order line item
 * @param {number} index - Position in order (optional)
 * @returns {Object} GA4 formatted item
 */
export const mapOrderLineItemToGA4Item = (orderLineItem, index = 0) => {
  return {
    item_id: orderLineItem.variant?.sku || '',
    item_name: orderLineItem.variant?.name || '',
    item_brand: orderLineItem.variant?.brand || '',
    item_category: orderLineItem.breadcrumb_taxons?.[0]?.name || '',
    item_category2: orderLineItem.breadcrumb_taxons?.[1]?.name || '',
    item_category3: orderLineItem.breadcrumb_taxons?.[2]?.name || '',
    item_variant: orderLineItem.variant?.option_values?.[0]?.name || '',
    price: parseFloat(orderLineItem.variant?.price) || 0,
    quantity: parseInt(orderLineItem.quantity, 10) || 1,
    index: index,
    coupon: orderLineItem.adjustments?.promotion?.find(adj => adj.label)?.label || ''
  };
};

/**
 * Map a promotion to GA4 promotion item format
 * 
 * @param {Object} promotion - The promotion data
 * @returns {Object} GA4 formatted promotion item
 */
export const mapPromotionToGA4Item = (promotion) => {
  return {
    item_id: promotion.sku || promotion.id || '',
    item_name: promotion.name || ''
  };
};

/**
 * Get product category path from breadcrumb taxons
 * 
 * @param {Array} breadcrumbTaxons - The breadcrumb taxons array
 * @returns {string} Category path
 */
export const getCategoryPath = (breadcrumbTaxons = []) => {
  if (!breadcrumbTaxons || !breadcrumbTaxons.length) return '';
  return breadcrumbTaxons.map(taxon => taxon.name).join('/');
};

/**
 * Format price for GA4
 * 
 * @param {string|number} price - The price to format
 * @returns {number} Formatted price
 */
export const formatPrice = (price) => {
  if (typeof price === 'number') return price;
  if (!price) return 0;
  
  return parseFloat(price.toString().replace(/[^0-9.]/g, '')) || 0;
}; 